'use client';

import { useMemo, useState, type ReactNode } from 'react';
import ResponsiveTableOfContents from './ResponsiveTableOfContents';
import ScrollToTopButton from './ScrollToTopButton';

interface Section {
  id: string;
  title: string;
  description?: string;
  category?: string;
  content: ReactNode;
}

export default function CheatsheetFilter({ sections, categories }: { sections: Section[]; categories: string[] }) {
  const [category, setCategory] = useState('all');
  const filtered = useMemo(() => category === 'all' ? sections : sections.filter(section => section.category === category), [category, sections]);
  const contents = useMemo(() => filtered.map(section => ({ id: section.id, label: section.title })), [filtered]);
  return (
    <>
      {categories.length > 1 && <div className="no-print mb-4 pb-4 border-b border-[var(--study-border)] print:hidden"><div className="flex flex-wrap gap-2">
        {categories.map(item => <button key={item} type="button" aria-pressed={category === item} onClick={() => {
          setCategory(item);
          requestAnimationFrame(() => document.getElementById('cheatsheet-items')?.scrollIntoView({ behavior: 'smooth', block: 'start' }));
        }} className={`px-3 sm:px-4 py-2 text-sm font-bold rounded-xl transition-colors ${category === item ? 'study-filter-active' : 'study-filter-idle'}`}>{item === 'all' ? 'All' : item}</button>)}
      </div></div>}
      {filtered.length > 0 ? <div className="grid items-start gap-6 lg:grid-cols-[17rem_minmax(0,1fr)] xl:grid-cols-[19rem_minmax(0,1fr)] print:block">
        <ResponsiveTableOfContents items={contents} title="Contents" itemNoun="sections" />
        <div id="cheatsheet-items" className="min-w-0 space-y-6 scroll-mt-28">
          {filtered.map((section, index) => <article key={section.id} id={section.id} className="scroll-mt-28 min-w-0">
            <div className="mb-4 flex items-start gap-3 sm:gap-4">
              <span className="study-link text-2xl sm:text-3xl font-bold leading-none pt-1">{index + 1}</span>
              <div className="min-w-0 flex-1">
                <h2 className="study-title text-xl sm:text-2xl font-bold mb-1">{section.title}</h2>
                {section.description && <p className="study-muted text-sm">{section.description}</p>}
                {section.category && category === 'all' && <span className="study-badge-soft inline-block mt-2 text-xs px-2 py-1 rounded">{section.category}</span>}
              </div>
            </div>
            <div className="study-cheatsheet-line pl-4">{section.content}</div>
          </article>)}
        </div>
      </div> : <p className="study-muted text-center py-12">No items found for the selected category.</p>}
      <ScrollToTopButton />
    </>
  );
}
